import React, { useEffect, useReducer } from "react";

import { setWithExpiry, getWithExpiry } from "lib/localStorage";

const LOCALSTORAGE_KEY = "murfy_reconditionne_cart_v7";

const EMPTY_STATE = {
  selection: [],
  shipping: {
    recoverOldDevice: false,
    method: null,
    address: {},
  },
  coupon: {
    id: null,
    code: "",
    expiration_date: null,
    discount_raw_amount: 0,
    discount_type: "",
  },
  open: false,
  collection_products: [],
  paymentRef: null,
  acceptedOptIn: false,
};

export const CartContext = React.createContext(null);

const reducer = (state, action) => {
  switch (action.type) {
    case "updateOptin": {
      // Update optin choice
      return {
        ...state,
        acceptedOptIn: action.acceptedOptIn,
      };
    }
    case "add": {
      // Find product in cart
      const index = state.selection.findIndex((i) => i.product.slug === action.product.slug);

      // If not already in cart, add it
      if (index === -1) {
        return {
          ...state,
          open: true,
          selection: [...state.selection, { product: action.product }],
        };
      }

      // If already in cart, update product
      return {
        ...state,
        selection: [
          ...state.selection.slice(0, index),
          {
            product: action.product,
          },
          ...state.selection.slice(index + 1),
        ],
      };
    }
    case "remove": {
      // Find product in cart
      const index = state.selection.findIndex((i) => i.product.slug === action.product.slug);

      // If not in cart, do nothing
      if (index === -1) {
        return state;
      }

      // If we remove last product, reset shipping options
      if (state.selection.length === 1) {
        return {
          ...state,
          open: true,
          selection: [],
          shipping: { recoverOldDevice: false, method: null, address: state.shipping.address },
          collection_products: [],
          coupon: {
            id: null,
            code: "",
            expiration_date: null,
            discount_raw_amount: 0,
            discount_type: "",
          },
        };
      }

      // Remove product from cart
      return {
        ...state,
        open: true,
        selection: [...state.selection.slice(0, index), ...state.selection.slice(index + 1)],
      };
    }
    case "updateWarranty": {
      // Find product in cart
      const index = state.selection.findIndex((i) => i.product.slug === action.product.slug);

      // If not in cart, do nothing
      if (index === -1) {
        return state;
      }

      // Change product warranty
      return {
        ...state,
        selection: [
          ...state.selection.slice(0, index),
          {
            product: action.product,
            warranty_name: action.warranty_name,
            warranty_price: action.warranty_price,
            warranty_years: action.warranty_years,
            warranty_pk: action.warranty_pk,
          },
          ...state.selection.slice(index + 1),
        ],
      };
    }
    case "updateShippingOptions": {
      // Find product in cart
      const index = state.selection.findIndex(({ product }) => product.slug === action.product?.slug);
      if (index === -1) {
        return state;
      }

      // Change product shipping attributes
      const updatedState = {
        ...state,
        selection: [
          ...state.selection.slice(0, index),
          {
            ...state.selection[index],
            delivery_option_id: action.delivery_option_id,
            delivery_option_type: action.delivery_option_type,
            shipping_price: action.shipping_price,
          },
          ...state.selection.slice(index + 1),
        ],
      };

      // get global shipping price
      const updatedPrice = updatedState.selection.reduce((total, item) => total + item.shipping_price, 0);

      return {
        ...updatedState,
        shipping: {
          ...state.shipping,
          price: updatedPrice,
          delivery_option_id: action.delivery_option_id,
          delivery_option_type: action.delivery_option_type,
        },
      };
    }

    case "updateShipping": {
      // Change shipping

      return {
        ...state,
        shipping: { ...state.shipping, ...action.shipping },
      };
    }

    case "updateCollectionProducts": {
      // Update products to collect
      return {
        ...state,
        collection_products: action.collection_products,
      };
    }

    case "addCoupon": {
      return {
        ...state,
        coupon: {
          id: action.coupon.id,
          code: action.coupon.code,
          expiration_date: action.coupon.expiration_date,
          discount_raw_amount: parseInt(action.coupon.discount_raw_amount),
          discount_type: action.coupon.discount_type,
        },
      };
    }
    case "removeCoupon": {
      return {
        ...state,
        coupon: {
          id: null,
          code: "",
          expiration_date: null,
          discount_raw_amount: 0,
          discount_type: "",
        },
      };
    }
    case "clear": {
      if (action.shippingOnly) {
        return {
          ...state,
          open: false,
          shipping: { recoverOldDevice: false, method: null, address: {} },
        };
      }
      return {
        ...state,
        coupon: {
          id: null,
          code: "",
          expiration_date: null,
          discount_raw_amount: 0,
          discount_type: "",
        },
        open: false,
        selection: [],
        collection_products: [],
        shipping: { recoverOldDevice: false, method: null, address: {} },
        paymentRef: null,
      };
    }
    case "load": {
      return {
        ...action.data,
        open: state?.open || false,
      };
    }
    case "open": {
      return {
        ...state,
        open: true,
      };
    }
    case "close": {
      return {
        ...state,
        open: false,
      };
    }
    default:
      throw new Error();
  }
};

const persistor = (state, action) => {
  const newState = reducer(state, action);
  if (action.type !== "load") {
    setWithExpiry(LOCALSTORAGE_KEY, newState);
  }
  return newState;
};

const CartProvider = ({ children }) => {
  const reducer = useReducer(persistor, null);
  const [, dispatch] = reducer;

  // sync cart between browser tabs
  useEffect(() => {
    function syncCart(event) {
      if (event.key !== LOCALSTORAGE_KEY) {
        return;
      }
      const data = getWithExpiry(LOCALSTORAGE_KEY);
      if (data) {
        dispatch({
          type: "load",
          data: { ...EMPTY_STATE, ...data },
        });
      }
    }
    window.addEventListener("storage", syncCart);
    return () => {
      window.removeEventListener("storage", syncCart);
    };
  }, [dispatch]);

  // Load cart from localStorage
  useEffect(() => {
    const data = getWithExpiry(LOCALSTORAGE_KEY);
    if (data) {
      dispatch({
        type: "load",
        data: { ...EMPTY_STATE, ...data },
      });
    } else {
      dispatch({
        type: "load",
        data: { ...EMPTY_STATE },
      });
    }
  }, [dispatch]);
  return <CartContext.Provider value={reducer}>{children}</CartContext.Provider>;
};

export default CartProvider;
